import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';

import { giveawayEmbedApi } from 'src/api';
import { SUPPORTED_LANGUAGES } from 'src/constants';

import './giveawayEmbed.scss';

const POLL_INTERVAL_MS = 5000;
const { Text } = Typography;

export const GiveawayEmbedView = () => {
  const { t, i18n } = useTranslation('giveaway_embed');
  const { uuid } = useParams();
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const [data, setData] = useState(null);

  useEffect(() => {
    const queryLanguage = new URLSearchParams(search).get('lang');
    const normalized = String(queryLanguage || '')
      .toLowerCase()
      .trim();

    if (!SUPPORTED_LANGUAGES.includes(normalized)) {
      return;
    }

    if (i18n.resolvedLanguage !== normalized && i18n.language !== normalized) {
      i18n.changeLanguage(normalized).catch((error) => {
        console.error('embed.changeLanguage[error]', error);
      });
    }
  }, [i18n, search]);

  const resolvedUuid =
    uuid ||
    (() => {
      const parts = String(pathname || '')
        .split('/')
        .filter(Boolean);

      if (parts.length >= 2 && parts[1] === 'embed') {
        return parts[0];
      }

      return '';
    })();

  const fetchEmbed = useCallback(async () => {
    if (!resolvedUuid) {
      return;
    }

    try {
      const response = await giveawayEmbedApi.fetchPublicGiveawayEmbed(resolvedUuid);
      const payload = response?.data?.data || null;

      if (!mountedRef.current) return;

      setData(payload);
    } catch (error) {
      if (!mountedRef.current) return;

      console.error('fetchPublicGiveawayEmbed[error]', error);

      const status = Number(error?.response?.status || 0);

      if (status === 404 || status === 400) {
        navigate('/', { replace: true });
      }
    }
  }, [navigate, resolvedUuid]);

  useEffect(() => {
    mountedRef.current = true;

    fetchEmbed();
    intervalRef.current = setInterval(fetchEmbed, POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fetchEmbed]);

  const title = data?.title || '—';
  const participantsCount = data?.participantsCount ?? '—';
  const remainingTickets = data?.remainingTickets ?? '—';
  const isFinished = data?.status === 'finished';

  const winnerValue =
    data?.winnerName && data?.winnerTicketNumber
      ? `${data.winnerName} #${data.winnerTicketNumber}`
      : data?.winnerName || '—';

  return (
    <div className="giveaway-embed-view">
      <div className="giveaway-embed-view__panel" aria-live="polite">
        <div className="giveaway-embed-view__cell">
          <Text className="giveaway-embed-view__label">{t('overlay.giveaway')}</Text>
          <Text className="giveaway-embed-view__value">{title}</Text>
        </div>

        <div className="giveaway-embed-view__divider" />

        <div className="giveaway-embed-view__cell giveaway-embed-view__cell--center">
          <Text className="giveaway-embed-view__label">{t('overlay.participants')}</Text>
          <Text className="giveaway-embed-view__value">{participantsCount}</Text>
        </div>

        <div className="giveaway-embed-view__divider" />

        <div className="giveaway-embed-view__cell giveaway-embed-view__cell--right">
          <Text className="giveaway-embed-view__label">
            {isFinished ? t('overlay.winner') : t('overlay.remaining_tickets')}
          </Text>
          <Text className="giveaway-embed-view__value">
            {isFinished ? winnerValue : remainingTickets}
          </Text>
        </div>
      </div>
    </div>
  );
};
